import PropTypes from 'prop-types';
import Constants from '../utils/Constants';

const {
    any,
    shape,
    arrayOf,
    string,
    number,
    bool,
    func,
    oneOf,
    oneOfType,
    object,
} = PropTypes;

export const extendedStateType = shape({
    // eslint-disable-next-line react/forbid-prop-types
    value: any,
    setValue: func,
    isChanged: bool,
    reset: func,
    changeInitialState: func,
});

export const passwordPolicyType = shape({
    passwordMinLength: number,
    passwordCheckSyntax: bool,
    passwordMinAlphas: number,
    passwordMinLowers: number,
    passwordMinUppers: number,
    passwordMinDigits: number,
    passwordMinCategories: number,
    passwordMinSpecials: number,
    passwordMin8bit: number,
    passwordMaxRepeats: number,
    passwordMinTokenLength: number,
    passwordHistory: bool,
    passwordInHistory: number,
});

export const modificationBlockersType = shape({
    blockerConstants: arrayOf(string),
    nonModifiableFields: arrayOf(string),
});

export const vhostType = shape({
    virtualHost: string,
    connectionStatus: string,
    nodeHostname: string,
});

export const vhostsType = arrayOf(vhostType);

export const uploadResponseDataType = shape({
    artifactId: number,
    outcome: string,
});

export const errorType = shape({
    name: string,
    message: string,
    statusCode: number,
});

export const graphQlErrorType = shape({
    message: string,
});

export const graphQLErrorsType = arrayOf(graphQlErrorType);

export const apolloErrorType = shape({
    graphQLErrors: graphQLErrorsType,
    networkError: errorType,
});

export const cancelTokenSourceType = shape({
    // eslint-disable-next-line react/forbid-prop-types
    token: object,
    // eslint-disable-next-line react/forbid-prop-types
    cancel: func,
});

export const fileUploadVariableType = shape({
    name: string,
    value: oneOfType([
        string,
        number,
        bool,
    ]),
});

export const fileUploadVariablesType = arrayOf(fileUploadVariableType);

export const extendedMutationType = shape({
    mutate: func,
    reset: func,
    isFourEyesNeeded: bool,
    loading: bool,
});

export const uiPhaseType = shape({
    serverPhase: string,
    id: string,
    icon: string,
    translate: func,
    is: func,
});
uiPhaseType.fromPhases = arrayOf(uiPhaseType);

export const countryType = shape({
    id: number,
    name: string,
    code: string,
    active: bool,
    deactivationBlockers: arrayOf(string),
    modificationBlockers: modificationBlockersType,
});

export const countriesType = arrayOf(countryType);

export const notificationType = shape({
    id: string,
    type: string,
    text: string,
});

export const notificationsType = arrayOf(notificationType);

export const currencyType = shape({
    code: string,
    name: string,
    exchangeRate: number,
    active: bool,
    deactivationBlockers: arrayOf(string),
    modificationBlockers: arrayOf(string),
});

export const currenciesType = arrayOf(currencyType);

export const zoneMemberType = shape({
    name: string,
    memberId: string,
});

export const zoneMembersType = arrayOf(zoneMemberType);

export const reducedUserType = shape({
    racf: string,
    userCode: string,
    name: string,
});

export const reducedUsersType = arrayOf(reducedUserType);

export const zoneType = shape({
    id: number,
    code: string,
    name: string,
    flagCode: string,
    active: bool,
    modificationBlockers: arrayOf(string),
    deactivationBlockers: arrayOf(string),
    currency: currencyType,
    // clearingHouse: clearingHouseType, - circular dependency, added later when clearingHouseType is declared
    assignedMembers: zoneMembersType,
    forbiddenUsers: reducedUsersType,
});

export const zonesType = arrayOf(zoneType);

export const productType = shape({
    id: number,
    active: bool,
    productType: string,
    name: string,
    productId: string,
    isinCode: string,
    iconSymbol: string,
    iconColor: string,
    deliveryType: string,
    deliveryPeriod: string,
    quantityUnit: string,
    deactivationBlockers: arrayOf(string),
    modificationBlockers: modificationBlockersType,
});

export const productsType = arrayOf(productType);

export const productPropertiesType = shape({
    availableDeliveryTypes: arrayOf(string),
    availableDeliveryPeriods: arrayOf(string),
    availableQuantityUnits: arrayOf(string),
    availableProductTypes: arrayOf(string),
});

export const timeZoneType = shape({
    ID: string,
});

export const defaultCashLimitType = shape({
    currencyName: string,
    currencyCode: string,
    cashLimit: number,
    isValid: bool,
});

export const defaultCashLimitsType = arrayOf(defaultCashLimitType);

export const clearingHouseCashLimitType = shape({
    cashLimit: number,
    currency: currencyType,
});

export const clearingHouseCashLimitsType = arrayOf(clearingHouseCashLimitType);

export const clearingHouseType = shape({
    id: number,
    name: string,
    code: string,
    timeZone: timeZoneType,
    zones: zonesType,
    cashLimits: clearingHouseCashLimitsType,
    modificationBlockers: arrayOf(string),
    deletionBlockers: arrayOf(string),
});

export const clearingHousesType = arrayOf(clearingHouseType);

// circular dependency
zoneType.clearingHouse = clearingHouseType;

export const clearingHousePropertiesType = shape({
    availableTimeZones: arrayOf(timeZoneType),
    availableCurrencies: currenciesType,
    minCashLimit: number,
    maxCashLimit: number,
});

export const memberCashLimitType = shape({
    clearingHouse: clearingHouseType,
    currency: currencyType,
    initialCashLimit: number,
    currentCashLimit: number,
    currentAuctionDay: string,
    nextAuctionDayInitialCashLimit: number,
    nextAuctionDay: string,
});

export const memberCashLimitsType = arrayOf(memberCashLimitType);

export const memberType = shape({
    name: string,
    memberId: string,
    memberType: string,
    emails: string,
    phone: string,
    suspended: bool,
    foreignCurrencyAllowed: bool,
    active: bool,
    euAuctioneer: bool,
    paymentCurrency: currencyType,
    balancingGroup: string,
    zones: zonesType,
    canBeDeleted: bool,
    deactivationBlockers: arrayOf(string),
    modificationBlockers: arrayOf(string),
    cashLimits: memberCashLimitsType,
});

export const membersType = arrayOf(memberType);

export const memberPropertiesType = shape({
    availableProductTypes: arrayOf(string),
    availableMemberTypes: arrayOf(string),
    availablePaymentCurrencies: currenciesType,
    availableZones: zonesType,
    clearingEnabled: bool,
    euAuctioneersEnabled: bool,
});

export const auctionResultType = shape({
    averageAcceptedPrice: number,
    lastAcceptedPrice: number,
    numberOfValidBids: number,
    indicativePrice: number,
    isUndersell: bool,
    totalBidQuantities: number,
    finalPrice: number,
    totalVolume: number,
});

export const auctionType = shape({
    id: number,
    name: string,
    phase: string,
    algorithm: string,
    quantity: number,
    openingTime: string,
    closureTime: string,
    fastMarket: bool,
    smallestUnit: number,
    spotReferencePrice: number,
    absoluteDeviation: number,
    deliveryStart: number,
    maxBidSize: number,
    minPrice: number,
    relativeDeviation: number,
    showLastPrice: bool,
    showRanking: bool,
    limitBidModification: bool,
    energyPerLot: number,
    description: string,
    power: number,
    maxPrice: number,
    deliveryYear: number,
    biddingType: string,
    auctionType: string,
    auctionResult: auctionResultType,
    lowestAcceptablePrice: number,
    highestAcceptablePrice: number,
    tradePriceCalculationType: string,
    auctionAcceptanceType: string,
    settlStartType: string,
    modificationBlockers: arrayOf(string),
    acceptanceBlockers: arrayOf(string),
    zone: zoneType,
    product: productType,
    auctioneerMember: memberType,
});

export const auctionsType = arrayOf(auctionType);

export const ldapStatusType = shape({
    statusId: string,
    params: arrayOf(string),
});

export const ldapStatusesType = arrayOf(ldapStatusType);

export const userType = shape({
    id: number,
    racf: string,
    userCode: string,
    name: string,
    email: string,
    roles: arrayOf(string),
    active: bool,
    suspended: bool,
    twoFAEnabled: bool,
    phone: string,
    address: string,
    modificationBlockers: arrayOf(string),
    suspensionBlockers: arrayOf(string),
    deactivationBlockers: arrayOf(string),
    activationBlockers: arrayOf(string),
    internal: bool,
    passwordRetryCount: string,
    lastSigninTime: string,
    ldapStatus: string,
    ldapStatuses: ldapStatusesType,
    forbiddenZones: zonesType,
    members: membersType,
});

export const usersType = arrayOf(userType);

export const bidType = shape({
    id: number,
    quantity: number,
    price: number,
    user: userType,
    member: memberType,
    lastModUser: string,
    lastModTime: string,
    creationTime: string,
    asBrokerReference: string,
    obfuscated: bool,
    cancelled: bool,
    cancellationReason: string,
    rank: number,
    quantityAllocated: number,
    volume: number,
    randomlyAllocated: bool,
    winner: bool,
    fromVisibleMember: bool,
});

export const bidsType = arrayOf(bidType);

export const sortingParamType = shape({
    key: string,
    direction: oneOf([Constants.SORT_ASC, Constants.SORT_DESC]),
});

export const bidModificationType = shape({
    bidId: number,
    quantity: number,
    price: number,
    asBrokerReference: string,
});

export const bidModificationsType = arrayOf(bidModificationType);

export const messageType = shape({
    id: number,
    messageKey: string,
    params: arrayOf(string),
    messageScope: string,
    lastUpdateTime: string,
});

export const messagesType = arrayOf(messageType);

export const checkboxListValueType = shape({
    id: string,
    label: string,
});

export const checkboxListValueTypes = arrayOf(checkboxListValueType);

export const availableRoleType = shape({
    role: string,
    isAssignable: bool,
    allowedMemberTypes: arrayOf(string),
    compatibleRoles: arrayOf(string),
});

export const availableRolesType = arrayOf(availableRoleType);

export const assignableMemberType = shape({
    memberType: string,
    members: membersType,
});

export const assignableMembersType = arrayOf(assignableMemberType);

export const allowedRoleCombinationsType = arrayOf(arrayOf(string));

export const userPropertiesType = shape({
    availableRoles: availableRolesType,
    availableForbiddenZones: zonesType,
    assignableMembers: assignableMembersType,
    allowedRoleCombinations: allowedRoleCombinationsType,
    clearingEnabled: bool,
});

export const reportType = shape({
    type: string,
    versions: arrayOf(string),
    formats: arrayOf(string),
    requiredFields: arrayOf(string),
});

export const reportsType = arrayOf(reportType);

export const zonePropertiesType = shape({
    availableCurrencies: currenciesType,
    availableClearingHouses: clearingHousesType,
});

export const reducedMemberType = shape({
    memberId: string,
    name: string,
    technicalAuctioneer: bool,
    regularAuctioneer: bool,
    euAuctioneer: bool,
});

export const reducedMembersType = arrayOf(reducedMemberType);

export const auctionLimitPropertiesType = shape({
    availableProducts: productsType,
    availableMembers: reducedMembersType,
    minAuctionLimit: number,
    maxAuctionLimit: number,
});

export const auctionLimitType = shape({
    id: number,
    limit: number,
    product: productType,
    member: reducedMemberType,
});

export const auctionLimitsType = arrayOf(auctionLimitType);

export const auctioneerDataTemplatePropertiesType = shape({
    availableAuctioneers: reducedMembersType,
    availableCountries: countriesType,
    maxQuantitySum: number,
});

export const auctioneerDataMatrixItemType = shape({
    auctioneer: reducedMemberType,
    country: countryType,
    quantity: number,
});

export const auctioneerDataMatrixItemsType = arrayOf(auctioneerDataMatrixItemType);

export const auctioneerDataTemplateType = shape({
    id: number,
    name: string,
    templateItems: auctioneerDataMatrixItemsType,
});

export const auctioneerDataTemplatesType = arrayOf(auctioneerDataTemplateType);

export const manualCashLimitType = shape({
    id: number,
    member: reducedMemberType,
    currency: currencyType,
    clearingHouse: clearingHouseType,
    creator: reducedUserType,
    limitValue: number,
    beginDate: string,
    endDate: string,
    creationTime: string,
    description: string,
});

export const holidayDateType = shape({
    id: oneOfType([number, string]),
    name: string,
    holidayDate: string,
});

export const holidayDatesType = arrayOf(holidayDateType);

export const holidayYearType = shape({
    year: number,
    holidays: arrayOf(holidayDateType),
});
export const holidayYearsType = arrayOf(holidayYearType);

export const holidayPropertiesType = shape({
    availableHolidayYears: holidayYearsType,
});

export const manualCashLimitsType = arrayOf(manualCashLimitType);

export const manualCashLimitPropertiesType = shape({
    availableMembers: reducedMembersType,
    availableCurrencies: currenciesType,
    availableClearingHouses: clearingHousesType,
    minCashLimit: number,
    maxCashLimit: number,
});

export const cashLimitType = shape({
    id: number,
    mainId: number,
    member: reducedMemberType,
    limitValue: number,
    beginDate: string,
    endDate: string,
    creator: string,
    status: string,
    version: number,
    token: string,
    definitionDescription: string,
    customName: string,
    ccp: string,
    tso: string,
    clearingMember: string,
});

export const cashLimitsType = arrayOf(cashLimitType);

export const auctionTemplateType = shape({
    id: number,
    name: string,
    product: productType,
    description: string,
    openingTime: string,
    closureTime: string,
    algorithm: string,
    showLastPrice: bool,
    showRanking: bool,
    limitBidModification: bool,
    minPrice: number,
    maxPrice: number,
    spotReferencePrice: number,
    absoluteDeviation: number,
    relativeDeviation: number,
    zone: zoneType,
    auctioneerMember: reducedMemberType,
    auctionType: string,
    biddingType: string,
    fastMarket: bool,
    auctionAcceptanceType: string,
    tradePriceCalculationType: string,
    quantity: number,
    deliveryYear: number,
    power: number,
    smallestUnit: number,
    deliveryStart: number,
    maxBidSize: number,
});

export const auctionPropertiesType = shape({
    availableProducts: productsType,
    availableZones: zonesType,
    availableAuctioneers: reducedMembersType,
    availableCountries: countriesType,
    availableAuctioneerDataTemplates: auctioneerDataTemplatesType,
    availableAuctionTypes: arrayOf(string),
    availableBiddingTypes: arrayOf(string),
    availableTradePriceCalculationTypes: arrayOf(string),
    availableAuctionAlgorithms: arrayOf(string),
    availableAuctionAcceptanceTypes: arrayOf(string),
    availableSettlStartTypes: arrayOf(string),
    availableAuctionTemplates: arrayOf(auctionTemplateType),
    defaultAuctionDurationHours: number,
});

export const auctionTemplatesType = arrayOf(auctionTemplateType);

export const requestRateLimitType = shape({
    limitId: oneOfType([number, string]),
    requestType: string,
    user: reducedUserType,
    value: number,
    messageRate: number,
});

export const requestRateLimitsType = arrayOf(requestRateLimitType);

export const requestRateLimitPropertiesType = shape({
    availableUsers: reducedUsersType,
    availableRequestTypes: arrayOf(string),
    period: number,
    defaultRateLimit: number,
});

export const clientApplicationType = shape({
    id: oneOfType([number, string]),
    applicationId: string,
    description: string,
    active: bool,
});

export const clientApplicationsType = arrayOf(clientApplicationType);

export const auctionTemplatePropertiesType = shape({
    availableBiddingTypes: arrayOf(string),
    availableAuctionAcceptanceTypes: arrayOf(string),
    availableTradePriceCalculationTypes: arrayOf(string),
    availableAuctionTypes: arrayOf(string),
    availableAuctionAlgorithms: arrayOf(string),
    availableSettlStartTypes: arrayOf(string),
    availableProducts: productsType,
    availableZones: zonesType,
    availableAuctioneers: reducedMembersType,
    defaultAuctionDurationHours: number,
});

export const cashLimitUploadEventType = shape({
    id: number,
    uploadTimestamp: string,
    fileName: string,
    exportTimestamp: string,
    uploader: reducedUserType,
    outcome: string,
    cashLimitConfirmationFileId: number,
});

export const cashLimitUploadEventsType = arrayOf(cashLimitUploadEventType);

export const biddingUserConfigurationType = shape({
    defaultPrice: number,
    defaultQuantity: number,
    priceStep: number,
    quantityStep: number,
    allowedBiddingTypes: arrayOf(string),
});

export const authenticationUserConfigurationType = shape({
    twoFAEnabled: bool,
    qrImage: string,
    secret: string,
});

export const auctionUserType = shape({
    racf: string,
    userCode: string,
    name: string,
    perms: arrayOf(string),
});

export const auctionUsersType = arrayOf(auctionUserType);

export const auctionMemberType = shape({
    name: string,
    memberId: string,
    auctionLimit: number,
    suspended: bool,
    currency: currencyType,
    users: auctionUsersType,
});

export const auctionMembersType = arrayOf(auctionMemberType);
